import { useEffect, useCallback, useRef } from 'react';
import { useNotifications } from './useNotifications';

const LAST_NOTIFICATION_KEY = 'evolve-last-daily-notification';
const REMINDER_HOUR = 7; // 7 AM local time
const EVENING_HOUR = 18; // 6 PM for evening check-in

interface ScheduledNotificationsConfig {
  userName?: string;
  motivation?: string;
}

export function useScheduledNotifications(config: ScheduledNotificationsConfig = {}) {
  const { isEnabled, sendNotification, getRandomQuote } = useNotifications();
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const getLastNotificationDate = useCallback(() => {
    const stored = localStorage.getItem(LAST_NOTIFICATION_KEY);
    return stored ? new Date(stored) : null;
  }, []);

  const setLastNotificationDate = useCallback(() => {
    localStorage.setItem(LAST_NOTIFICATION_KEY, new Date().toISOString());
  }, []);

  const shouldSendDailyNotification = useCallback(() => {
    const now = new Date();
    const lastNotification = getLastNotificationDate();
    
    // If never sent, or sent on a different day
    if (!lastNotification) return true;
    
    const lastDate = lastNotification.toDateString();
    const todayDate = now.toDateString();
    
    return lastDate !== todayDate;
  }, [getLastNotificationDate]);

  const sendDailyMotivationalNotification = useCallback(() => {
    if (!isEnabled) return;
    
    const { quote, cta } = getRandomQuote();
    const now = new Date();
    const hour = now.getHours();
    
    let title = '💪 Gym Time';
    let body = `"${quote}" — ${cta}`;
    
    // Personalize based on time of day
    if (hour < 12) {
      title = '🌅 Morning Motivation';
      body = config.userName 
        ? `Rise and grind, ${config.userName}! "${quote}" — ${cta}`
        : `Rise and grind! "${quote}" — ${cta}`;
    } else if (hour >= 17) {
      title = '🌙 Evening Check-in';
      body = config.motivation
        ? `Remember why you started: ${config.motivation}. "${quote}"`
        : `"${quote}" — Did you move today?`;
    }
    
    sendNotification(title, body);
    setLastNotificationDate();
  }, [isEnabled, sendNotification, getRandomQuote, config.userName, config.motivation, setLastNotificationDate]);

  const checkAndSendScheduledNotification = useCallback(() => {
    if (!isEnabled) return;
    
    const now = new Date();
    const hour = now.getHours();
    
    // Check if it's around reminder time (7 AM or 6 PM) and we haven't sent today
    const isReminderTime = hour === REMINDER_HOUR || hour === EVENING_HOUR;
    
    if (isReminderTime && shouldSendDailyNotification()) {
      sendDailyMotivationalNotification();
    }
  }, [isEnabled, shouldSendDailyNotification, sendDailyMotivationalNotification]);

  // Set up periodic check when app is running
  useEffect(() => {
    if (!isEnabled) return;

    // Check immediately on mount
    checkAndSendScheduledNotification();

    // Check every 30 minutes
    checkIntervalRef.current = setInterval(() => {
      checkAndSendScheduledNotification();
    }, 30 * 60 * 1000); // 30 minutes

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [isEnabled, checkAndSendScheduledNotification]);

  // Manual trigger for testing
  const sendTestNotification = useCallback(() => {
    if (!isEnabled) return;
    
    const { quote, cta } = getRandomQuote();
    const name = config.userName || 'Evolver';
    
    sendNotification(
      '🔥 Test: Daily Motivation',
      `Hey ${name}! "${quote}" — ${cta}`
    );
  }, [isEnabled, sendNotification, getRandomQuote, config.userName]);

  return {
    sendDailyMotivationalNotification,
    sendTestNotification,
    checkAndSendScheduledNotification,
  };
}
